import {
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { Subject, takeUntil } from 'rxjs';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatSuffix,
	MatError,
} from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf } from '@angular/common';

@Component({
	selector: 'formly-collector-selector',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<mat-form-field appearance="outline" style="width: 100%">
			<mat-label>
				{{ props.label }}
			</mat-label>
			<input
				matInput
				[value]="collectorName"
				readonly
				[required]="props.required"
				[disabled]="props.disabled"
				[placeholder]="props.placeholder || 'Select collector'"
				style="cursor: pointer;"
				(click)="!props.disabled && agencyId && onButtonClick()"
			/>
			<button
				mat-icon-button
				matSuffix
				type="button"
				(click)="onButtonClick()"
				[disabled]="props.disabled || !agencyId"
			>
				<mat-icon>contacts</mat-icon>
			</button>
			<mat-error *ngIf="showError">
				<formly-validation-message [field]="field"></formly-validation-message>
			</mat-error>
		</mat-form-field>
	`,
	imports: [
		MatFormField,
		MatLabel,
		MatInput,
		MatIconButton,
		MatSuffix,
		MatIcon,
		NgIf,
		MatError,
		FormlyModule,
	],
})
export class CollectorSelectorType
	extends FieldType<FieldTypeConfig>
	implements OnInit, OnDestroy
{
	private readonly destroy$ = new Subject<void>();

	constructor(private readonly cdr: ChangeDetectorRef) {
		super();
	}

	get showError(): boolean {
		return !!(
			this.formControl.invalid &&
			(this.formControl.touched || this.formControl.dirty)
		);
	}

	get collectorName(): string {
		const value = this.formControl.value;
		if (!value) {
			return '';
		}

		return value.name;
	}

	get agencyId(): string {
		// Get agency ID from the model
		// For main form, use Account_Name
		// For Additional_Debt, use Agency from the same row
		if (this.model?.Agency) {
			return this.model.Agency;
		}
		if (this.model?.Account_Name) {
			return this.model.Account_Name;
		}
		return '';
	}

	get agencyName(): string {
		// Get agency name from props if available
		if (this.props['agencyName']) {
			return this.props['agencyName'];
		}

		// Try to get from getAgencyName callback
		if (this.props['getAgencyName']) {
			const name = this.props['getAgencyName'](this.agencyId);
			if (name) {
				return name;
			}
		}

		return 'Selected Agency';
	}

	ngOnInit() {
		// Subscribe to value changes to trigger change detection
		this.formControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.cdr.markForCheck();
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onButtonClick(): void {
		if (!this.agencyId) {
			return;
		}

		// Get the collector object from the current value
		let selectedCollector = this.formControl.value;

		// Create agency object
		const agency = {
			id: this.agencyId,
			Account_Name: this.agencyName,
		};

		// Call the parent component's callback to handle modal
		if (this.props['onCollectorButtonClick']) {
			this.props['onCollectorButtonClick'](
				agency,
				selectedCollector,
				this.field
			);
		}
	}
}
